import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import cartIcon from '../assets/shopping-bag-icon.svg';
import { cartLineEligibleForSubscriberDiscount, subscriberDiscountEligibleSubtotal } from '../utils/subscriberDiscount';
import { CHECKOUT_ENABLED } from '../data/featureFlags';

export default function CartDrawer() {
  const navigate = useNavigate();
  const { 
    cartItems, 
    removeFromCart,
    updateQuantity,
    clearCart, 
    getTotalItems, 
    getTotalPrice,
    isDrawerOpen,
    setIsDrawerOpen
  } = useCart();
  const { isSubscriber, discountPercentage } = useSubscription();
  const subtotal = getTotalPrice();
  const eligibleSubtotal = subscriberDiscountEligibleSubtotal(cartItems);
  const discountAmount =
    isSubscriber && discountPercentage > 0 ? eligibleSubtotal * (discountPercentage / 100) : 0;
  const grandTotal = Math.max(0, subtotal - discountAmount);

  const handleClose = () => {
    setIsDrawerOpen(false);
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  useEffect(() => {
    if (!isDrawerOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (e) => {
      if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isDrawerOpen]);

  const drawerContent = (
    <>
      {isDrawerOpen && (
        <div
          className="cart-drawer-backdrop"
          onClick={handleBackdropClick}
          aria-hidden="true"
        />
      )}

      <div
        className={`cart-drawer ${isDrawerOpen ? 'open' : ''}`}
        aria-hidden={!isDrawerOpen}
        role="dialog"
        aria-modal={isDrawerOpen}
        aria-label="Shopping cart"
      >
        <div className="cart-drawer-header">
          <div className="d-flex align-items-center gap-2">
            <img 
              src={cartIcon} 
              alt="Cart" 
              style={{ width: '24px', height: '24px' }}
            />
            <h4 className="mb-0 bold primary_purple">Cart ({getTotalItems()})</h4>
          </div>
          <button
            className="cart-drawer-close_btn"
            onClick={handleClose}
            aria-label="Close cart"
          >
            <svg 
              width="24" 
              height="24" 
              viewBox="0 0 24 24" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
            >
              <g id="Menu / Close_MD">
                <path 
                  id="Vector" 
                  d="M18 18L12 12M12 12L6 6M12 12L18 6M12 12L6 18" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
              </g>
            </svg>
          </button>
        </div>

        <div className="cart-drawer-body">
          {cartItems.length === 0 ? (
            <div className="text-center py-5">
              <svg 
                width="120" 
                height="120" 
                viewBox="0 0 24 24" 
                fill="none" 
                xmlns="http://www.w3.org/2000/svg"
                style={{ marginBottom: '2rem' }}
              >
                <path d="M9 17C9.85038 16.3697 10.8846 16 12 16C13.1154 16 14.1496 16.3697 15 17" stroke="rgb(109, 78, 236)" strokeWidth="1.5" strokeLinecap="round"/>
                <ellipse cx="15" cy="10.5" rx="1" ry="1.5" fill="rgb(109, 78, 236)"/>
                <ellipse cx="9" cy="10.5" rx="1" ry="1.5" fill="rgb(109, 78, 236)"/>
                <path d="M7 3.33782C8.47087 2.48697 10.1786 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 10.1786 2.48697 8.47087 3.33782 7" stroke="rgb(109, 78, 236)" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              <h5 className="mb-2 primary_purple bold">Your cart is empty</h5>
              <p className="mb-0 cart-drawer-summary-muted">Add items to your cart to get started!</p>
            </div>
          ) : (
            <>
              <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2">
                <span className="cart-drawer-summary-muted">
                  {getTotalItems()} item(s) in your cart
                </span>
                {cartItems.length > 0 && (
                  <button 
                    className="btn btn-sm text-danger"
                    onClick={clearCart}
                    style={{ fontSize: '14px', padding: '4px 12px', fontWeight: 'normal' }}
                  >
                    Clear All
                  </button>
                )}
              </div>

              {cartItems.map((item) => {
                // Handle both cases: item.product (from local) or item.product_id (from MySQL)
                const product = item.product || {};
                const productName = product.name || `Product #${item.product_id || 'Unknown'}`;
                const productImage = product.image || 'https://via.placeholder.com/80';
                const productPrice = Number(item.amount || product.prices?.retail?.amount || 0);
                const lineDiscount =
                  isSubscriber &&
                  discountPercentage > 0 &&
                  cartLineEligibleForSubscriberDiscount(item);
                const discountedPrice = lineDiscount
                  ? productPrice * (1 - discountPercentage / 100)
                  : productPrice;
                const displayPrice = lineDiscount ? discountedPrice : productPrice;
                const lineTotal = displayPrice * item.quantity;
                
                const handleQuantityChange = (newQuantity) => {
                  if (newQuantity < 1) {
                    removeFromCart(item.id);
                  } else {
                    updateQuantity(item.id, newQuantity);
                  }
                };
                
                return (
                  <div key={item.id} className="d-flex gap-3 mb-3 pb-3 border-bottom">
                    <img 
                      src={productImage} 
                      alt={productName}
                      style={{ 
                        width: '80px', 
                        height: '80px', 
                        objectFit: 'contain', 
                        borderRadius: '5px',
                        backgroundColor: '#f8f8f8',
                        padding: '5px'
                      }}
                    />
                    <div className="flex-grow-1">
                      <p className="mb-1 bold cart-drawer-item_name">{productName}</p>
                      <p className="mb-1 cart-drawer-item_price">
                        {lineDiscount ? (
                          <><span style={{ textDecoration: 'line-through', marginRight: '4px' }}>${productPrice.toFixed(2)}</span><span className="text-success">${displayPrice.toFixed(2)}</span></>
                        ) : (
                          `$${productPrice.toFixed(2)}`
                        )}{' x '}{item.quantity}
                      </p>
                      <p className="mb-2 cart-drawer-item_total">
                        Total: ${lineTotal.toFixed(2)}
                      </p>
                      <div className="d-flex align-items-center justify-content-between gap-2">
                        {/* Quantity Controls */}
                        <div className="d-flex align-items-center gap-2">
                          <button
                            className="btn btn-sm cart-drawer-qty_btn"
                            style={{ width: '28px', height: '28px', padding: 0 }}
                            onClick={() => handleQuantityChange(item.quantity - 1)}
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
                              <path d="M6 12L18 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </button>
                          <span className="cart-drawer-qty_value">{item.quantity}</span>
                          <button
                            className="btn btn-sm cart-drawer-qty_btn"
                            style={{ width: '28px', height: '28px', padding: 0 }}
                            onClick={() => handleQuantityChange(item.quantity + 1)}
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
                              <path d="M4 12H20M12 4V20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </button>
                        </div>
                        <button
                          className="btn btn-sm text-danger"
                          style={{ fontSize: '14px', padding: '4px 12px', fontWeight: 'normal' }}
                          onClick={() => removeFromCart(item.id)}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>

        {/* Footer with Total and Buttons */}
        {cartItems.length > 0 && (
          <div className="cart-drawer-footer">
            <div className="d-flex justify-content-between align-items-center mb-1 cart-drawer-summary-muted">
              <span>Subtotal:</span>
              <span>${subtotal.toFixed(2)}</span>
            </div>
            {discountAmount > 0 && (
              <div className="d-flex justify-content-between align-items-center mb-1 primary_purple cart-drawer-item_price">
                <span>Subscription Discount:</span>
                <span className="text-success">−${discountAmount.toFixed(2)}</span>
              </div>
            )}
            <div className="d-flex justify-content-between align-items-center mb-3 mt-1">
              <strong className="cart-drawer-summary-label">Grand Total:</strong>
              <strong className="primary_purple cart-drawer-summary-total">
                ${grandTotal.toFixed(2)}
              </strong>
            </div>
            <div className="d-flex gap-2 flex-column">
              <button
                type="button"
                className="home_button_purple no_arrow cart-drawer-checkout-btn"
                disabled={!CHECKOUT_ENABLED}
                onClick={() => {
                  if (!CHECKOUT_ENABLED) return;
                  navigate('/checkout');
                  handleClose();
                }}
              >
                Proceed to Checkout
              </button>
              <button
                type="button"
                className="cart-drawer-outline-btn"
                onClick={() => {
                  navigate('/cart');
                  handleClose();
                }}
              >
                View Cart
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );

  return createPortal(drawerContent, document.body);
}

